import { Head, router } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import LogForm from "./_form"
import type { ExpenseFormData, TimeEntryFormData } from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Option {
  id: number
  name: string
}

interface ServiceItemType {
  id: number
  name: string
  cost: number
  price: number
}

interface PaymentType {
  id: number
  name: string
}

interface ExpenseType {
  id: number
  name: string
}

interface LogData {
  log_type: string
  content: string
  title: string
  request: string
  request_date: string | null
  log_date: string
  due_date: string | null
  results_notes: string
  assigned_to_id: number | null
  significant: boolean
  sensitive: boolean
  urgent: boolean
  customer_user_id: number | null
  vendor_id: number | null
  todo_category_id: number | null
  user_id: number | null
}

interface NotifiableUser {
  id: number
  name: string
}

interface Props {
  case_record: CaseRecord
  log: LogData
  users: Option[]
  todo_categories: Option[]
  can_create_restricted: boolean
  can_create_accounting: boolean
  errors?: Record<string, string[]>
  case_open?: boolean
  service_item_types?: ServiceItemType[]
  payment_types?: PaymentType[]
  expense_types?: ExpenseType[]
  notifiable_users?: NotifiableUser[]
  case_manager?: NotifiableUser | null
}

export default function New({
  case_record,
  log,
  users,
  todo_categories,
  can_create_restricted,
  can_create_accounting,
  errors,
  case_open,
  service_item_types,
  payment_types,
  expense_types,
  notifiable_users,
  case_manager,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Logs", href: `/cases/${case_record.id}/logs` },
    { title: "New", href: `/cases/${case_record.id}/logs/new` },
  ]

  const { data, setData, processing } = useForm<LogData>({
    log_type: log.log_type,
    content: log.content,
    title: log.title,
    request: log.request,
    request_date: log.request_date,
    log_date: log.log_date,
    due_date: log.due_date,
    results_notes: log.results_notes,
    assigned_to_id: log.assigned_to_id,
    significant: log.significant,
    sensitive: log.sensitive,
    urgent: log.urgent,
    customer_user_id: log.customer_user_id,
    vendor_id: log.vendor_id,
    todo_category_id: log.todo_category_id,
    user_id: log.user_id,
  })

  const [timeEntryData, setTimeEntryData] = useState<TimeEntryFormData>({
    service_item_type_id: null,
    payment_type_id: null,
    recorded_hours: 0,
    service_hourly_cost: 0,
    service_hourly_price: 0,
    mileage: 0,
    mileage_rate: 0.58,
    mileage_bill_rate: 0.67,
    travel_hours: 0,
    travel_rate: 0,
    billable: true,
    notes: "",
    vehicle_allowance: "",
  })

  const [notifyUserIds, setNotifyUserIds] = useState<number[]>([])

  const [expenseData, setExpenseData] = useState<ExpenseFormData>({
    expense_type_ref_id: null,
    amount: "",
    notes: "",
    paid_by: "",
    paid_to: "",
    approval_notes: "",
  })

  const handleTimeEntryChange = (
    key: keyof TimeEntryFormData,
    value: number | string | boolean | null,
  ) => {
    setTimeEntryData((prev) => ({ ...prev, [key]: value }))
  }

  const handleExpenseChange = (
    key: keyof ExpenseFormData,
    value: number | string | boolean | null,
  ) => {
    setExpenseData((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    router.post(`/cases/${case_record.id}/logs`, {
      case_log: { ...data },
      time_entry: { ...timeEntryData },
      expense: { ...expenseData },
      ...(data.log_type === "update" || data.log_type === "todo"
        ? { notify_user_ids: notifyUserIds }
        : {}),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`New Log - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">New Log</h1>
        <LogForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Log"
          users={users}
          todo_categories={todo_categories}
          can_create_restricted={can_create_restricted}
          can_create_accounting={can_create_accounting}
          caseOpen={case_open}
          serviceItemTypes={service_item_types}
          paymentTypes={payment_types}
          expenseTypes={expense_types}
          timeEntryData={timeEntryData}
          onTimeEntryChange={handleTimeEntryChange}
          expenseData={expenseData}
          onExpenseChange={handleExpenseChange}
          notifiableUsers={notifiable_users}
          caseManager={case_manager}
          notifyUserIds={notifyUserIds}
          onNotifyUserIdsChange={setNotifyUserIds}
        />
      </div>
    </AppLayout>
  )
}
